import { Button } from '@components/Button'
import { useAdminPreview } from '@hooks/useAdminPreview'
import { HEADER_CONTENT_TOP_SPACING } from '@constants/layout'
import { useGetChannel } from '@services/api/graphql/channels'
import { useChannelMarkAsRead } from '@services/api/graphql/mutations/channelMarkAsRead'
import { useCallback, useEffect, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { ActivityIndicator } from 'react-native'
import { useToastController } from '@tamagui/toast'
import { Text, YStack } from 'tamagui'
import { MessageReplyForm } from './MessageReplyForm'
import { MessageThread } from './MessageThread'

type ToastType = 'success' | 'error'

interface IChannelConversationProps {
  channelId: string
}

export function ChannelConversation({ channelId }: IChannelConversationProps) {
  const { t } = useTranslation('Messages')
  const toast = useToastController()
  const { data, loading, error, refetch } = useGetChannel({ id: channelId })
  const { handleMarkAsRead } = useChannelMarkAsRead()
  const { isPreview } = useAdminPreview()

  const handleToast = useCallback(
    (type: ToastType, message: string) => {
      toast?.show?.(message, { type })
    },
    [toast],
  )

  const channel = data?.channel
  const messages = useMemo(() => channel?.messages ?? [], [channel?.messages])

  useEffect(() => {
    // Escrita silenciosa: marcar como lido dispararia sem nenhum clique. Em
    // preview o produtor não deve alterar o estado das mensagens do aluno.
    if (isPreview) return
    if (channel?.id) handleMarkAsRead(channel.id)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [channel?.id, isPreview])

  if (loading && !channel) {
    return (
      <YStack alignItems="center" py="$spacing-inset-lg">
        <ActivityIndicator size="large" />
      </YStack>
    )
  }

  if (error || !channel) {
    return (
      <YStack
        alignItems="center"
        gap="$spacing-inset-xs"
        py="$spacing-inset-lg"
        testID="channel-conversation-error"
      >
        <Text
          fontFamily="$mulishRegular"
          fontSize="$font-size-xxs"
          color="$danger-300"
          textAlign="center"
        >
          {t('loadError')}
        </Text>
        <Button variant="secondary" size="small" onPress={() => refetch()}>
          {t('retry')}
        </Button>
      </YStack>
    )
  }

  return (
    <YStack
      marginTop={HEADER_CONTENT_TOP_SPACING}
      gap="$spacing-inset-md"
      paddingVertical="$spacing-inset-xs"
      paddingHorizontal="$size.spacing-xs"
      $gtSm={{ paddingHorizontal: '$size.spacing-xsl' }}
      testID="channel-conversation"
    >
      <Text
        fontFamily="$mulishBold"
        fontSize="$font-size-md"
        color="$accent"
        fontWeight="bold"
      >
        {channel.subject}
      </Text>
      <MessageThread messages={messages} />
      <MessageReplyForm
        channelId={channel.id}
        onReplied={() => refetch()}
        onToast={handleToast}
      />
    </YStack>
  )
}
